import type {
  GetIntegrationsResponse,
  GetIntegrationUsersResponse,
  GetPackageResponse,
  GetTokenStatusResponse,
  GetVendorsResponse,
  LinksResponse,
} from '~/api/esign/typedefs.autogen';
import type {
  ESigGetIntegrationUsersResponse,
  ESigIntegrationsResponse,
  EsignatureIntegrationType,
  ESigVendorsResponse,
} from '~/features/admin/ESignature/types';
import type {
  EsignatureAuthenticateResponse,
  EsignatureTokenStatus,
  EsignatureTokenStatusValue,
} from '~/features/ticket/types/esignature';
import type { Nullable } from '~/types/Nullable';

import { endpoints as autogenEnhancements } from '~/api/esign/enhancements.autogen';
import { Tags } from '~/api/esign/tags.autogen';
import { EsignatureType } from '~/enums/EsignatureType';
import { onEsignCacheEntryAdded } from '~/redux/api/subscriptions/esign';

import type { EsignPackage } from './packageTypes';

import { mapEsignError } from './mapError';
import { esignApiRoot } from './root';

const NOT_FOUND = 404;

const mapTokenStatus = (
  response: GetTokenStatusResponse,
): EsignatureTokenStatus => {
  const { attributes } = response.data;
  return {
    integrationType:
      attributes.integration_type.toLowerCase() as EsignatureIntegrationType,
    providerType: attributes.vendor.toLowerCase() as EsignatureType,
    accountToken: attributes.account_token as EsignatureTokenStatusValue,
    envelopeToken:
      (attributes.envelope_token as Nullable<EsignatureTokenStatusValue>) ??
      null,
  };
};

// Apply the schema-derived providesTags / invalidatesTags for any generated
// endpoints that are not explicitly overridden below.
esignApiRoot.enhanceEndpoints({ endpoints: autogenEnhancements });

/**
 * Consumer-facing esign endpoints. These override the raw generated endpoints
 * (`overrideExisting: true`) to reproduce the exact runtime behavior of the
 * legacy hand-written esignature-service methods: the same friendly argument
 * and result shapes, the same response mapping, the same error mapping to
 * `CodedError`, the same cache tags/invalidation, and no automatic error toast
 * (consumers surface their own errors).
 */
/**
 * @lintignore Not imported by name anywhere, but this export must exist: dropping it
 * (or knip's fix un-exporting/deleting it) risks the module never being evaluated, which
 * would mean `injectEndpoints` never registers these endpoints on the shared root api.
 */
export const esignApi = esignApiRoot.injectEndpoints({
  overrideExisting: true,
  endpoints: (build) => ({
    getTokenStatus: build.query<
      Nullable<EsignatureTokenStatus>,
      { ticketId?: string }
    >({
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: '/api/v1/tokens/status',
          method: 'get',
          params: arg.ticketId ? { ticket_id: arg.ticketId } : undefined,
          rtkQueryOptions: { showErrorToast: false },
        });

        if (result.error) {
          const { status } = result.error as { status?: number | string };
          // No connected e-signature integration: the server returns 404.
          if (status === NOT_FOUND) {
            return { data: null };
          }
          return { error: mapEsignError(result.error) };
        }

        return { data: mapTokenStatus(result.data as GetTokenStatusResponse) };
      },
      providesTags: [{ type: Tags.Tokens, id: 'LIST' }],
    }),

    authorize: build.query<
      EsignatureAuthenticateResponse,
      { return_to: string }
    >({
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: '/api/v1/authorize',
          method: 'post',
          body: {
            data: {
              type: 'authorizations',
              attributes: { return_url: arg.return_to },
            },
          },
          rtkQueryOptions: { showErrorToast: false, withCredentials: true },
        });

        if (result.error) {
          const error = result.error as {
            status?: number | string;
            data?: { links?: { authorize?: Nullable<string> } };
          };
          // Preserve the legacy 302-with-link special case.
          if (error.status === 302 && error.data?.links?.authorize) {
            return { data: { authenticateUrl: error.data.links.authorize } };
          }
          return { error: mapEsignError(result.error) };
        }

        const response = result.data as LinksResponse;
        return { data: { authenticateUrl: response.links.authorize ?? null } };
      },
    }),

    getIntegrations: build.query<ESigIntegrationsResponse, void>({
      query: () => ({
        url: '/api/v1/integrations',
        method: 'get',
        rtkQueryOptions: { showErrorToast: false },
      }),
      transformResponse: (response: GetIntegrationsResponse) =>
        response.data as unknown as ESigIntegrationsResponse,
      transformErrorResponse: mapEsignError,
      providesTags: (result) => [
        { type: Tags.Integrations, id: 'LIST' },
        ...(result?.map((integration) => ({
          type: Tags.Integrations,
          id: integration.id,
        })) ?? []),
      ],
    }),

    getVendors: build.query<ESigVendorsResponse, void>({
      query: () => ({
        url: '/api/v1/vendors',
        method: 'get',
        rtkQueryOptions: { showErrorToast: false },
      }),
      transformResponse: (response: GetVendorsResponse) =>
        response.data as unknown as ESigVendorsResponse,
      transformErrorResponse: mapEsignError,
    }),

    getIntegrationUsers: build.query<
      ESigGetIntegrationUsersResponse,
      { integrationId: string }
    >({
      query: ({ integrationId }) => ({
        url: `/api/v1/integrations/${integrationId}/users`,
        method: 'get',
        rtkQueryOptions: { showErrorToast: false },
      }),
      transformResponse: (response: GetIntegrationUsersResponse) =>
        response.data as unknown as ESigGetIntegrationUsersResponse,
      transformErrorResponse: mapEsignError,
    }),

    getPackage: build.query<EsignPackage, string>({
      query: (ticketId) => ({
        url: '/api/v1/packages',
        method: 'get',
        params: { ticket_id: ticketId },
        rtkQueryOptions: { showErrorToast: false },
      }),
      transformResponse: (response: GetPackageResponse) =>
        response.data as unknown as EsignPackage,
      transformErrorResponse: mapEsignError,
      // Ticket-scoped (not LIST): invalidate only the package for the ticket
      // that received the Pusher event. Autogen LIST tags were dropped when this
      // override replaced the generated endpoint — restore a usable tag here.
      providesTags: (_result, _error, ticketId) => [
        { type: Tags.Packages, id: ticketId },
      ],
    }),

    /**
     * Dummy RTK Query endpoint whose sole job is to own the Pusher lifecycle for
     * `private-esign-ticket-{clientId}-{ticketId}`. Mount once per ticket
     * page/surface (e.g. SignTaskItem); do not hang this off getPackage or
     * useEsignVendor.
     */
    subscribeToEsignUpdates: build.query<
      null,
      { ticketId: string; clientId: number }
    >({
      queryFn: () => ({ data: null }),
      // Tear down the Pusher channel as soon as the last subscriber unmounts
      // (don't inherit the esign API's 5-minute keepUnusedDataFor).
      keepUnusedDataFor: 0,
      onCacheEntryAdded: onEsignCacheEntryAdded,
    }),

    createIntegration: build.mutation<
      void,
      { integrationType: EsignatureIntegrationType; vendor: EsignatureType }
    >({
      query: ({ integrationType, vendor }) => ({
        url: '/api/v1/integrations',
        method: 'post',
        body: {
          data: {
            type: 'integrations',
            attributes: {
              type: integrationType.toUpperCase(),
              vendor: vendor.toUpperCase(),
            },
          },
        },
        rtkQueryOptions: { showErrorToast: false },
      }),
      transformErrorResponse: mapEsignError,
      invalidatesTags: [
        { type: Tags.Integrations, id: 'LIST' },
        { type: Tags.Tokens, id: 'LIST' },
      ],
    }),

    updateIntegrationType: build.mutation<
      void,
      { integrationId: string; integrationType: EsignatureIntegrationType }
    >({
      query: ({ integrationId, integrationType }) => ({
        url: `/api/v1/integrations/${integrationId}`,
        method: 'patch',
        body: {
          data: {
            type: 'integrations',
            attributes: { type: integrationType.toUpperCase() },
          },
        },
        rtkQueryOptions: { showErrorToast: false },
      }),
      transformErrorResponse: mapEsignError,
      invalidatesTags: (_result, _error, { integrationId }) => [
        { type: Tags.Integrations, id: integrationId },
        { type: Tags.Integrations, id: 'LIST' },
        { type: Tags.Tokens, id: 'LIST' },
      ],
    }),

    deleteIntegration: build.mutation<void, { integrationId: string }>({
      query: ({ integrationId }) => ({
        url: `/api/v1/integrations/${integrationId}`,
        method: 'delete',
        rtkQueryOptions: { showErrorToast: false },
      }),
      transformErrorResponse: mapEsignError,
      invalidatesTags: (_result, _error, { integrationId }) => [
        { type: Tags.Integrations, id: integrationId },
        { type: Tags.Integrations, id: 'LIST' },
        { type: Tags.Tokens, id: 'LIST' },
      ],
    }),

    deleteIntegrationUserTokens: build.mutation<
      void,
      { integrationId: string; token_ids: string[] }
    >({
      query: ({ integrationId, token_ids }) => ({
        url: `/api/v1/integrations/${integrationId}/users`,
        method: 'delete',
        body: {
          data: {
            type: 'auth-identities',
            attributes: { token_ids },
          },
        },
        rtkQueryOptions: { showErrorToast: false },
      }),
      transformErrorResponse: mapEsignError,
      onQueryStarted: async (
        { integrationId, token_ids },
        { dispatch, queryFulfilled },
      ) => {
        const patchResult = dispatch(
          esignApi.util.updateQueryData(
            'getIntegrations',
            undefined,
            (draft) => {
              const integration = draft.find((i) => i.id === integrationId);
              if (integration) {
                integration.attributes.authenticated_user_count = Math.max(
                  integration.attributes.authenticated_user_count -
                    token_ids.length,
                  0,
                );
              }
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
  }),
});
